import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ChevronRight } from 'lucide-react';
import { useOffers, type OfferRow } from '@/hooks/data/useOffers';
import { CreateOfferStepper } from '@/components/offers/CreateOfferStepper';
import { formatDate, formatNumber } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/offers')({
  component: OffersPage,
});

const STATUS_PILL: Record<OfferRow['status'], string> = {
  draft: 'fm-pill-neutral',
  sent: 'fm-pill-info',
  accepted: 'fm-pill-pos',
  rejected: 'fm-pill-dgr',
  expired: 'fm-pill-dgr',
  signed: 'fm-pill-pos',
  contract_signed: 'fm-pill-pos',
  closed: 'fm-pill-neutral',
  // Batch AN — seller pushed back; waiting on buyer to sign the Gegenanbot.
  counter_offered: 'fm-pill-warn',
};

const STATUS_LABEL: Record<OfferRow['status'], string> = {
  draft: 'Entwurf',
  sent: 'Gesendet',
  accepted: 'Akzeptiert',
  rejected: 'Abgelehnt',
  expired: 'Abgelaufen',
  signed: 'Unterzeichnet',
  contract_signed: 'Vertrag unterzeichnet',
  closed: 'Geschlossen',
  counter_offered: 'Gegenanbot gestellt',
};

function daysLeft(expiresAt: number | string | null | undefined): { label: string; cls: string } {
  if (expiresAt == null) return { label: '—', cls: 'text-ink-3' };
  const ts = typeof expiresAt === 'number' ? expiresAt : new Date(expiresAt).getTime();
  if (Number.isNaN(ts)) return { label: '—', cls: 'text-ink-3' };
  const now = Date.now();
  const diff = ts - now;
  if (diff < 0) return { label: 'abgelaufen', cls: 'text-[hsl(var(--danger))]' };
  const days = Math.ceil(diff / 86_400_000);
  if (days <= 3) return { label: `${days} Tag${days === 1 ? '' : 'e'}`, cls: 'text-[hsl(var(--warning))]' };
  return { label: `${days} Tage`, cls: 'text-ink-3' };
}

function OffersPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const { data: items = [], isLoading } = useOffers(
    status ? { status: status as OfferRow['status'] } : {},
  );

  const stats = useMemo(() => {
    const draft = items.filter((o) => o.status === 'draft').length;
    const sent = items.filter((o) => o.status === 'sent').length;
    const accepted = items.filter(
      (o) => o.status === 'accepted' || o.status === 'signed' || o.status === 'contract_signed',
    ).length;
    const total = items.length;
    return { total, draft, sent, accepted };
  }, [items]);

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('offers.title', 'Befristete Kaufanbote')}
          </h2>
          <p className="text-sm text-ink-3">
            {t('offers.subtitle', 'Anbote in 5 Schritten erstellen, PDF generieren, archivieren.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90"
        >
          <Plus className="h-4 w-4" />
          {t('offers.create.cta', 'Neues Kaufanbot')}
        </button>
      </header>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('offers.stats.total', 'Gesamt')}</p>
          <p className="fm-kpi-value">{formatNumber(stats.total)}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('offers.stats.draft', 'Entwurf')}</p>
          <p className="fm-kpi-value">{stats.draft}</p>
        </div>
        <div className="fm-kpi">
          <p className="fm-kpi-label">{t('offers.stats.sent', 'Gesendet')}</p>
          <p className="fm-kpi-value">{stats.sent}</p>
        </div>
        <div className="fm-kpi !border-ink !bg-ink">
          <p className="fm-kpi-label !text-gold">{t('offers.stats.accepted', 'Unterzeichnet')}</p>
          <p className="fm-kpi-value !text-paper">{stats.accepted}</p>
        </div>
      </div>

      <div className="fm-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          >
            <option value="">{t('offers.filters.all', 'Alle Status')}</option>
            <option value="draft">{t('enums.offer_status.draft', 'Entwurf')}</option>
            <option value="sent">{t('enums.offer_status.sent', 'Gesendet')}</option>
            <option value="accepted">{t('enums.offer_status.accepted', 'Akzeptiert')}</option>
            <option value="rejected">{t('enums.offer_status.rejected', 'Abgelehnt')}</option>
            <option value="expired">{t('enums.offer_status.expired', 'Abgelaufen')}</option>
            <option value="signed">{t('enums.offer_status.signed', 'Unterzeichnet')}</option>
          </select>
        </div>

        {/* Mobile card list */}
        <div className="space-y-2 md:hidden">
          {isLoading && (
            <p className="py-12 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
          )}
          {!isLoading && items.length === 0 && (
            <p className="py-12 text-center text-sm text-ink-3">
              {t('offers.empty', 'Keine Kaufanbote.')}
            </p>
          )}
          {items.map((o) => {
            const left = daysLeft(o.expires_at);
            return (
              <Link
                key={o.id}
                to="/offers/$id"
                params={{ id: o.id }}
                className="block rounded-md border border-line bg-card p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-[11px] text-ink-3">{o.id.slice(0, 8)}</p>
                    <p className="mt-0.5 font-medium text-ink">
                      € {formatNumber((o.offered_price / 100), { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <span className={`fm-pill shrink-0 ${STATUS_PILL[o.status]}`}>
                    {t(`enums.offer_status.${o.status}`, STATUS_LABEL[o.status])}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs">
                  <span className="text-ink-3">
                    {t('offers.cols.created', 'Erstellt')}{' '}
                    {formatDate(new Date(o.created_at))}
                  </span>
                  <span className={left.cls}>{left.label}</span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden overflow-x-auto md:block">
          <table className="fm-table">
            <thead>
              <tr>
                <th>{t('offers.cols.created', 'Erstellt')}</th>
                <th className="text-end">{t('offers.cols.price', 'Preis')}</th>
                <th>{t('offers.cols.expires', 'Läuft ab')}</th>
                <th>{t('offers.cols.status', 'Status')}</th>
                <th className="text-end">{t('offers.cols.actions', 'Aktion')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="text-center text-ink-3">
                    {t('common.loading', 'Lädt …')}
                  </td>
                </tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-ink-3">
                    {t('offers.empty', 'Keine Kaufanbote.')}
                  </td>
                </tr>
              )}
              {items.map((o) => {
                const left = daysLeft(o.expires_at);
                return (
                  <tr key={o.id}>
                    <td>
                      <p className="font-medium text-ink">
                        {formatDate(new Date(o.created_at))}
                      </p>
                      <p className="font-mono text-[11px] text-ink-3">{o.id.slice(0, 8)}</p>
                    </td>
                    <td className="text-end font-medium">
                      € {formatNumber((o.offered_price / 100), {
                        maximumFractionDigits: 0,
                      })}
                    </td>
                    <td>
                      <p>{formatDate(new Date(o.expires_at))}</p>
                      <p className={`text-xs ${left.cls}`}>{left.label}</p>
                    </td>
                    <td>
                      <span className={`fm-pill ${STATUS_PILL[o.status]}`}>
                        {t(`enums.offer_status.${o.status}`, STATUS_LABEL[o.status])}
                      </span>
                    </td>
                    <td className="text-end">
                      <Link
                        to="/offers/$id"
                        params={{ id: o.id }}
                        className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-3 py-1 text-xs font-medium text-ink hover:bg-paper-2"
                      >
                        {t('offers.actions.open', 'Öffnen')}
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CreateOfferStepper open={creating} onOpenChange={setCreating} />
    </section>
  );
}
